import { faCalendar } from "@fortawesome/pro-regular-svg-icons/faCalendar";
import { faExclamationCircle } from "@fortawesome/pro-regular-svg-icons/faExclamationCircle";
import { faExclamationTriangle } from "@fortawesome/pro-regular-svg-icons/faExclamationTriangle";
import { faQuestionCircle } from "@fortawesome/pro-solid-svg-icons/faQuestionCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, Typography } from "@mui/material";
import { red } from '@mui/material/colors';
import blue from '@mui/material/colors/blue';
import * as api from "api/Core";
import { GlobalClassKeys } from "assets/jss/common-styles";
import { SpinnerIndicator } from "components/CallServer";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import DashDoughnut from "components/Dashboard/DashDoughnut";
import GenericList from "components/GenericList";
import { JoinComponents } from "components/JoinComponents";
import MuiBox from "components/MuiExtras/MuiBox";
import MuiClickable from "components/MuiExtras/MuiClickable";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiLink from "components/MuiExtras/MuiLink";
import MuiColor from "components/MuiExtras/Typography/MuiColor";
import MuiMuted from "components/MuiExtras/Typography/MuiMuted";
import { KnowledgeTestDifficultyLevel, SystemRoles, TopicDashboardKnowledgeDatabaseSummaryColumn } from "models/ApiEnums";
import { GenericListItemDto, TopicDashboardKnowledgeDatabaseModel, TopicShortViewModel } from "models/ApiModels";
import * as React from "react";
import { Bar } from "react-chartjs-2";
import { WithTranslation } from "react-i18next";
import Session from "services/Session";
import { KnowledgeTestLevelName } from "views/Dashboards/components/KnowledgeCommons";
import CreateTest from "views/KnowledgeTest/CreateTest";

/////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    topic: TopicShortViewModel;
}

interface IState {
    data: TopicDashboardKnowledgeDatabaseModel;
    lastError: any;
    loading: boolean;
    dialog: any;
}

type Props = IProps & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////

export class TopicDashboardKnowledgeDatabase extends React.Component<Props, IState>{

    constructor(props: Props) {
        super(props);

        this.state = {
            data: null,
            dialog: null,
            lastError: null,
            loading: true,
        };
    }

    ///
    private res(r: string, f: any = null) {
        return this.props.t("Views.Dashboards.Topic.KnowledgeDatabase." + r, f);
    }

    ///
    private loadData() {
        api.getTopicDashboardKnowledgeDatabase({
            topicId: this.props.topic.topicId,
            columnToSummary: TopicDashboardKnowledgeDatabaseSummaryColumn.Resume,
            range: null
        })
            .then((result) => {
                this.setState({ data: (result as TopicDashboardKnowledgeDatabaseModel), loading: false });
            })
            .catch((error) => {
                this.setState({ data: null, lastError: error, loading: false });
            });
    }

    ///
    private buildRecomendation(level: KnowledgeTestDifficultyLevel, newItems: number) {
        return (
            <MuiContainer key={level} margin="dense">
                <div>
                    <MuiLink color="secondary" onClick={() => { this.createTest(level); }}>{this.res("CreateTestLabel")} <b><KnowledgeTestLevelName value={level} /></b></MuiLink>
                </div>
                <div>
                    <MuiMuted>{this.res("CreateTestSublabel", { count: newItems })}</MuiMuted>
                </div>
            </MuiContainer>
        );
    }

    ///
    private createTest(level: KnowledgeTestDifficultyLevel) {
        this.setState({
            dialog: (
                <CreateTest
                    initialTopic={
                        {
                            key: this.props.topic.topicId,
                            label: this.props.topic.name,
                            thumbnailUrl: this.props.topic.thumbnailUrl,
                            canDelete: true
                        }
                    }
                    initialDifficultyLevel={level}
                    onClose={
                        () => this.setState({ dialog: null }, () => this.loadData())
                    }
                />
            )
        });
    }

    ///
    private handleShowTests = () => {
        this.setState({
            dialog: <GenericList
                title={this.res("ShowTestsTitle")}
                showThumbnail={true}
                variant="drawer"
                onGetData={
                    (searchText: string, offset: number, take: number) => api.getTopicDashboardKnowledgeDatabase(
                        {
                            topicId: this.props.topic.topicId,
                            columnToSummary: TopicDashboardKnowledgeDatabaseSummaryColumn.TestBellow50Detail,
                            searchText: searchText,
                            range: { offset, take }
                        }
                    )}

                onRenderName={
                    (item: GenericListItemDto) => {
                        return (
                            <MuiClickable
                                disabled={!Session.hasRole(SystemRoles.TrainingManager)}
                                onClick={
                                    () =>
                                        this.setState({
                                            dialog: (
                                                <CreateTest
                                                    testId={this.props.topic.topicId}
                                                    onClose={
                                                        () => this.setState({ dialog: null })
                                                    }
                                                />
                                            )
                                        })
                                }
                            >
                                {item.name}
                            </MuiClickable>
                        );
                    }
                }

                onClose={() => this.setState({ dialog: null })}
            />
        });
    };

    ///
    private renderHeader() {
        const { loading } = this.state;
        return (
            <MuiContainer>
                <MuiBox variant="notice">
                    <Grid container justifyContent="space-between" alignItems="center" >
                        <Grid item>

                            <MuiColor variant="success">
                                <Grid container alignItems="center" spacing={2} >
                                    <Grid item>
                                        <FontAwesomeIcon icon={faQuestionCircle} size="4x" />
                                    </Grid>
                                    <Grid item className={GlobalClassKeys.textCenter}>
                                        {
                                            loading
                                                ? <SpinnerIndicator />
                                                : <Typography variant="h5">{this.state.data.totalTests}</Typography>
                                        }
                                        <JoinComponents components={this.res("TotalTestsText").split('\n')} separator={<br />} />
                                    </Grid>
                                </Grid>
                            </MuiColor>

                        </Grid>

                        <Grid item>

                            <MuiColor variant="success">
                                <Grid container alignItems="center" spacing={2} >
                                    <Grid item>
                                        <FontAwesomeIcon icon={faExclamationCircle} size="4x" />
                                    </Grid>
                                    <Grid item className={GlobalClassKeys.textCenter}>
                                        {
                                            loading
                                                ? <SpinnerIndicator />
                                                : <Typography variant="h5">{this.state.data.totalTestsHighPriority}</Typography>
                                        }
                                        <span>{this.res("TotalTestsHighPriorityText")}</span>
                                    </Grid>
                                </Grid>
                            </MuiColor>

                        </Grid>


                        <Grid item>

                            <MuiColor variant="warning">
                                <Grid container alignItems="center" spacing={2} >
                                    <Grid item>
                                        <FontAwesomeIcon icon={faCalendar} size="4x" />
                                    </Grid>
                                    <Grid item className={GlobalClassKeys.textCenter}>
                                        {
                                            loading
                                                ? <SpinnerIndicator />
                                                : <Typography variant="h5">{this.state.data.totalFutureTests}</Typography>
                                        }
                                        <JoinComponents components={this.res("TotalFutureTestsText").split('\n')} separator={<br />} />
                                    </Grid>
                                </Grid>
                            </MuiColor>

                        </Grid>
                    </Grid>
                </MuiBox >
            </MuiContainer>
        );
    }

    ///
    private renderGauges() {
        const { loading } = this.state;

        return (
            <MuiContainer>
                <Grid container alignItems="center" >

                    <Grid item>
                        {
                            loading
                                ? <SpinnerIndicator />
                                : (
                                    <div>
                                        <div className={GlobalClassKeys.textCenter}>
                                            <MuiColor variant="error">
                                                <Typography variant="button"><b>{this.res("GaugeTitle")}</b></Typography>
                                            </MuiColor>
                                        </div>

                                        <MuiClickable onClick={this.handleShowTests} disabled={this.state.data.indexTestBellow50 === 0}>
                                            <div>
                                                <DashDoughnut value={this.state.data.indexTestBellow50} color={red[500]} />
                                            </div>
                                        </MuiClickable>
                                    </div>
                                )
                        }
                    </Grid>

                    {
                        !loading &&
                        <Grid item xs>
                            {this.renderRecommendation()}
                        </Grid>
                    }
                </Grid>
            </MuiContainer >
        );
    }

    ///
    private renderRecommendation() {
        const { data: { recBeginner, recIntermediate, recAdvanced } } = this.state;

        const arr = [];
        if (recBeginner > 0) arr.push(this.buildRecomendation(KnowledgeTestDifficultyLevel.Beginner, recBeginner));
        if (recIntermediate > 0) arr.push(this.buildRecomendation(KnowledgeTestDifficultyLevel.Intermediate, recIntermediate));
        if (recAdvanced > 0) arr.push(this.buildRecomendation(KnowledgeTestDifficultyLevel.Advanced, recAdvanced));

        if (arr.length > 0) {
            return (
                <div>
                    <MuiContainer>
                        <MuiBox variant="mutedLight" border={false}>
                            <MuiColor variant="warning">
                                <Grid container spacing={2}>
                                    <Grid item>
                                        <FontAwesomeIcon icon={faExclamationTriangle} size="2x" />
                                    </Grid>
                                    <Grid item>
                                        <Typography variant="h6">{this.res("RecommendationsText")}</Typography>
                                    </Grid>
                                </Grid>
                            </MuiColor>
                        </MuiBox>
                    </MuiContainer>

                    {arr}
                </div>
            );
        }
        return null;
    }

    ///
    private renderDistribution() {

        if (this.state.loading) {
            return <SpinnerIndicator />;
        }

        const { data: { levelBeginner, levelIntermediate, levelAdvanced } } = this.state;
        const ds = [levelBeginner, levelIntermediate, levelAdvanced];
        const total = ds.reduce((p, c) => p + c);

        return (
            <MuiContainer>

                <div className={GlobalClassKeys.textCenter}>
                    <Typography variant="h5"><MuiColor variant="mutedDark">{this.res("DistributionTitle")}</MuiColor></Typography>
                </div>

                <div style={{ height: 150 }} >
                    <Bar
                        data={
                            {
                                labels: [this.res("DistributionChartLabel")],
                                datasets: [
                                    {
                                        label: this.props.t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.KnowledgeRange1"),
                                        backgroundColor: blue[100],
                                        data: [ds[0] / total * 100.0]
                                    },
                                    {
                                        label: this.props.t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.KnowledgeRange2"),
                                        backgroundColor: blue[300],
                                        data: [ds[1] / total * 100.0]
                                    },
                                    {
                                        label: this.props.t("Views.Dashboards.UserEvolution.Knowledge.CreateGraph.KnowledgeRange3"),
                                        backgroundColor: blue[500],
                                        data: [ds[2] / total * 100.0]
                                    }
                                ]
                            }
                        }
                        options={
                            {
                                indexAxis: 'y',
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: {
                                    legend: {
                                        display: true,
                                        position: 'bottom',
                                    },
                                    tooltip: {
                                        callbacks: {
                                            label: function (item: any) {
                                                return `${item.chart.data.datasets[item.datasetIndex].label}: ${parseFloat(item.formattedValue).toFixed(1)}% (${ds[item.datasetIndex]})`;
                                            },
                                        }
                                    }
                                },
                                scales: {
                                    x: {
                                        display: true,
                                        stacked: true,
                                        grid: {
                                            display: true
                                        },
                                        title: {
                                            display: true,
                                        },
                                        min: 0,
                                        max: 100,
                                        ticks: {
                                            callback: function (value: number) {
                                                return value.toFixed(0) + '%'; // convert it to percentage
                                            },
                                        }
                                    },
                                    y: {
                                        display: false,
                                        stacked: true,
                                        title: {
                                            display: true,
                                        },
                                    }
                                }
                            }
                        }
                    />
                </div>
            </MuiContainer>
        );
    }

    ///
    public componentDidMount() {
        this.loadData();
    }

    ///
    public render() {
        const { dialog, lastError } = this.state;

        return (
            <>
                {
                    lastError
                        ? <RequestErrorReport error={lastError} />
                        : (
                            <div>
                                {this.renderHeader()}
                                {this.renderGauges()}
                                {this.renderDistribution()}
                                {dialog}
                            </div>
                        )
                }
            </>
        );
    }
}